import React, {useEffect, useRef, useState} from "react"
import {useForm, ValidationError} from "@formspree/react"
import {Button, Form, Grid, Icon, Image, Message, Modal,} from "semantic-ui-react"
import {getImageAsset} from "./api/api"
import countries from "i18n-iso-countries"
import enCountries from "i18n-iso-countries/langs/en.json"
import esCountries from "i18n-iso-countries/langs/es.json"
import plCountries from "i18n-iso-countries/langs/pl.json"
import {i18nMessages} from "./locale"
import {FormattedMessage, useIntl} from "react-intl"


// Register country locales
countries.registerLocale(enCountries)
countries.registerLocale(esCountries)
countries.registerLocale(plCountries)

type ContactFormProps = {
    open: boolean
    onClose: () => void
    onI18nLanguage: (lang: string) => void
}

export function ContactForm({ open, onClose, onI18nLanguage }: ContactFormProps) {
    const intl = useIntl()
    const [state, handleSubmit, reset] = useForm(process.env.REACT_APP_CREATOR_TRIAL!)
    const [imageAsset, setImageAsset] = useState("/assets/contact-form.png")
    const i18nCountryNames = countries.getNames(intl.locale, { select: "official" })
    const sortedCountries = Object
        .entries(i18nCountryNames)
        .sort(([, nameA], [, nameB]) => nameA.localeCompare(nameB, intl.locale))
    const formRef = useRef<HTMLFormElement>(null)
    const [formData, setFormData] = useState({name: "", email: "", country: "", message: ""})
    const isFormValid = formData.name?.trim() !== "" && formData.email?.trim() !== "" && formData.country?.trim() !== "" && formData.message?.trim() !== ""
    const i18nLanguages = Object.keys(i18nMessages).sort()

    /**
     * When submission succeeds, update cooldown timer in localStorage.
     */
    useEffect(() => {
        if (state.succeeded) sessionStorage.setItem("lastSubmitTime", Date.now().toString())
    }, [state.succeeded])

    /**
     * Submit handler with cooldown enforcement.
     */
    const handleSubmitWithCooldown = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!submitCooldown()) return
        const asset = await getImageAsset(formData.message)
        if (asset) return setImageAsset(`${asset}?v=${Date.now()}`)
        await handleSubmit(event)
    }

    /**
     * 1 minute cooldown check.
     */
    const submitCooldown = () => {
        const last = sessionStorage.getItem("lastSubmitTime")
        if (!last) return true
        return Date.now() - Number(last) > 60_000
    }

    /**
     * Update on input change.
     */
    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setFormData((prev) => ({ ...prev, [name]: value }))
    }

    /**
     * Wrap onClose to reset form state before closing modal.
     */
    const onCloseReset = () => {
        reset() // reset Formspree state
        Object.keys(formData).forEach(field => formData[field] = "")
        setImageAsset("/assets/contact-form.png")
        onClose()
    }

    function getFlagEmoji(countryCode: string) {
        return countryCode.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    }

    return (
        <Modal closeIcon open={open} onClose={onCloseReset} size="large" dimmer="blurring" transition={{ animation: "fade", duration: 1 }}>
            <Modal.Header style={{ marginTop: "30px" }}>
                <Icon name="comments" size="large"/>&nbsp;<FormattedMessage id="contact.title"/><br/>
            </Modal.Header>
            <Modal.Content>
                <Grid stackable columns={2} className="contact-form-grid">
                    <Grid.Column width={7} textAlign="center" verticalAlign="top">

                        {/* Reasons */}
                        <div className="contact-reasons">
                            <p>⚠️&nbsp;<FormattedMessage id="contact.reason.bug"/></p>
                            <p>ℹ️&nbsp;<FormattedMessage id="contact.reason.services"/></p>
                            <p>💡&nbsp;<FormattedMessage id="contact.reason.change"/></p>
                            <p>🧭&nbsp;<FormattedMessage id="contact.reason.ux"/></p>
                            <p>🤝&nbsp;<FormattedMessage id="contact.reason.partnering"/></p>
                            <br/>
                            <FormattedMessage id="contact.reason.share"/>
                        </div>

                        {/* Image */}
                        <Image
                            id="contact-form-image"
                            style={!imageAsset.includes("contact-form") ? { marginTop: 10 } : {}}
                            src={imageAsset}
                        />

                    </Grid.Column>
                    {/* Form */}
                    <Grid.Column width={7} className="contact-form-column" verticalAlign="top">
                        <Form ref={formRef} onSubmit={handleSubmitWithCooldown} style={{ position: "relative" }}>
                            <Form.Field >
                                <label htmlFor="name"><FormattedMessage id="contact.name"/></label>
                                <input id="name" name="name" type="text" onChange={onChange} placeholder={
                                    intl.formatMessage({id: "contact.name.placeholder"})
                                } value={formData.name ?? ""}/>
                            </Form.Field>

                            <Form.Field>
                                <label htmlFor="email"><FormattedMessage id="contact.email"/></label>
                                <input id="email" name="email" type="email" onChange={onChange} placeholder="you@email.com" value={formData.email ?? ""}/>
                                <ValidationError prefix="Email" field="email" errors={state.errors}/>
                            </Form.Field>

                            <Form.Field>
                                <label htmlFor="country">
                                    <FormattedMessage id="contact.country"/>
                                </label>
                                <div className="narrow-select">
                                    <select id="country" name="country" className="narrow-select" onChange={onChange} value={formData.country ?? ""}>
                                        <option value="">
                                            {intl.formatMessage({id: "contact.country.placeholder"})}
                                        </option>
                                            {sortedCountries.map(([code, name]) =>
                                                <option key={code} value={code}>{name} {getFlagEmoji(code)}</option>
                                            )}
                                    </select>
                                </div>
                                <ValidationError prefix="Country" field="country" errors={state.errors}/>
                            </Form.Field>

                            <Form.Field>
                                <label htmlFor="message">
                                    <FormattedMessage id="contact.message"/>
                                </label>
                                <Form.TextArea id="message" name="message" rows={8} onChange={onChange} placeholder={
                                    intl.formatMessage({id: "contact.message.placeholder"})
                                } value={formData.message ?? ""}/>
                                <ValidationError prefix="Message" field="message" errors={state.errors}/>
                            </Form.Field>

                            {state.succeeded ? (
                                <Message positive className="contact-message" content={
                                    <FormattedMessage id="contact.success.body"/>
                                }/>
                            ) : (
                                <>
                                    <Button primary fluid type="submit" disabled={state.submitting || !isFormValid}>
                                        <FormattedMessage id="contact.submit"/>
                                    </Button>
                                    {!submitCooldown() && (
                                        <Message negative className="contact-message cooldown-message" content={
                                            <FormattedMessage id="contact.cooldown"/>
                                        }/>
                                    )}
                                </>
                            )}
                        </Form>
                    </Grid.Column>
                </Grid>
            </Modal.Content>

            {/* We speak... */}
            <div id="we-speak">
                <FormattedMessage id="contact.we.speak"/>&nbsp;
                {i18nLanguages.map((lang, idx) => (
                    <span key={lang}>
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a onClick={() => onI18nLanguage(lang)}><FormattedMessage id={`i18n.language.${lang}`}/></a>
                        {idx < i18nLanguages.length - 1 ? ", " : ""}
                    </span>
                ))}
            </div>
        </Modal>
    )
}
